import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Modal } from "@/components/modal/modal";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "actions/modal";
import {
  findPlanByPlanCodeName,
  isLegacyPlan,
  useUpdateUserPlan,
} from "./plan-utils";
import {
  GenericLegacyPlanContent,
  resolvePlanByFeature,
  UpsellContent,
} from "./upsell-content";
import { getUser } from "selectors/commonSelectors";
import {
  PlanCurrency,
  PlanPeriodUnit,
  SkedPlanCodeName,
  UpsellFeatureType,
  UpsellModalType,
} from "./types";
import { User } from "features/user";
import { UpsellOrderSummary } from "./upsell-order-summary";
import { useFetchedPlans, useUserSubscriptionAccountCount } from "./plan-api";
import { UpsellOrderSuccess } from "./upsell-order-success";
import { UpsellPlansContext } from "./upsell-plans";
import {
  GenericErrorMessage,
  toastCollabUpsellError,
} from "./common-components";
import { Spinner } from "@/components/ui/spinner";
import { isUserCollaborator } from "utils/user";
import { UpsellTrial } from "./upsell-trial";

// Keep upsell above content/create-post modals rendered from a different
// registry/root, plus the sticky subscription-banner (tailwind `z-50`) and
// any sticky footers.
//
// Base z-indexes come from `lib/modal-stack`:
//   backdrop  = Z_INDEX_MODAL_BACKDROP  (1055)
//   container = Z_INDEX_MODAL_CONTAINER (1056)
//   content   = Z_INDEX_MODAL_CONTENT   (1060)
//   +step     = Z_INDEX_STEP            (+10 per stacked modal)
//
// With this +200 offset, the upsell modal renders at 1255 / 1256 / 1260
// — above a base modal (1060), above 20 stacked modals (unrealistic),
// above shadcn popovers/dropdowns (1100), and above tooltips (1200).
// Bump the offset if any of those anchors move.
const UPSELL_MODAL_Z_INDEX_OFFSET = 200;

export const PlanUpsellModal = ({
  isLegacyPlan,
  triggerFeature,
  downgrade,
}: {
  isLegacyPlan: boolean;
  triggerFeature: UpsellFeatureType;
  downgrade: boolean;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const user = useSelector(getUser) as User;
  const currentAccountCount = useUserSubscriptionAccountCount();
  const [step, setStep] = useState<"plans-info" | "order-summary" | "result">(
    "plans-info"
  );
  const { plans, isError, isLoading } = useFetchedPlans();
  const { subscription } = user.subscription;
  const userPlan = user.plan as SkedPlanCodeName;
  const toPlan = resolvePlanByFeature(triggerFeature);
  const foundPlan = findPlanByPlanCodeName(
    plans,
    subscription.billing_period_unit as PlanPeriodUnit,
    toPlan
  );

  const { updateUserPlanContext } = useUpdateUserPlan();
  const planContext: UpsellPlansContext = {
    plans: plans,
    currency: subscription.currency_code,
    periodUnit: subscription.billing_period_unit as PlanPeriodUnit,
    isLegacyPlan: isLegacyPlan,
    isLoading: isLoading,
    isError: isError,
    currentPlan: userPlan,
    onUpgradeClick: () => {
      if (isUserCollaborator(user)) {
        toastCollabUpsellError(user);
        return;
      }
      setStep("order-summary");
    },
    includeGst: !!foundPlan?.includesGst,
    triggerFeature,
  };

  if (!toPlan || !foundPlan || isLegacyPlan) {
    return (
      <BaseUpsellModal>
        <UpsellContent plansContext={planContext} user={user} />
      </BaseUpsellModal>
    );
  }

  return (
    <BaseUpsellModal>
      {(() => {
        switch (step) {
          case "plans-info": {
            return <UpsellContent plansContext={planContext} user={user} />;
          }
          case "order-summary": {
            return (
              <UpsellOrderSummary
                context={{
                  currency: subscription.currency_code as PlanCurrency,
                  periodUnit:
                    subscription.billing_period_unit as PlanPeriodUnit,
                  numberOfAccounts: currentAccountCount || 1,
                  planId: foundPlan.id,
                  planName: toPlan,
                  extraAccountPrice: foundPlan.additionalAccountsPrice,
                  isDowngrade: downgrade,
                }}
                onBack={() => {
                  setStep("plans-info");
                }}
                onSuccess={({ numberOfAccounts }, subscription) => {
                  updateUserPlanContext({
                    numberOfAccounts,
                    toPlan,
                    subscription,
                  });

                  setStep("result");
                }}
              />
            );
          }
          case "result": {
            return (
              <UpsellOrderSuccess
                feature={triggerFeature}
                planName={toPlan}
                downgrade={downgrade}
              />
            );
          }
          default: {
            return null;
          }
        }
      })()}
    </BaseUpsellModal>
  );
};

export const AddAccountUpsellModal = () => {
  return <BaseUpsellModal>Upsell Add account</BaseUpsellModal>;
};

export const PlanUpsellDirectModal = ({
  toPlan,
  period,
  numberOfAccounts,
  downgrade,
}: {
  toPlan?: SkedPlanCodeName;
  period?: PlanPeriodUnit;
  numberOfAccounts?: number;
  downgrade: boolean;
}) => {
  const user = useSelector(getUser) as User;
  const currentAccountCount = useUserSubscriptionAccountCount();
  const [step, setStep] = useState<"order-summary" | "result">("order-summary");
  const { plans, isError, isLoading } = useFetchedPlans();
  const { subscription } = user.subscription;
  const { updateUserPlanContext } = useUpdateUserPlan();
  const [isChangingToYearly, setIsChangingToYearly] = useState(false);

  useEffect(() => {
    if (period === "year" && subscription.billing_period_unit === "month") {
      setIsChangingToYearly(true);
    }
  }, [subscription, period]);

  if (isLoading || !currentAccountCount) {
    return (
      <BaseUpsellModal>
        <FixedContainer>
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        </FixedContainer>
      </BaseUpsellModal>
    );
  }

  if (isError) {
    return (
      <BaseUpsellModal>
        <FixedContainer>
          <GenericErrorMessage />
        </FixedContainer>
      </BaseUpsellModal>
    );
  }

  const currentPeriod = subscription.billing_period_unit as PlanPeriodUnit;

  period = period || currentPeriod;
  const resolvedToPlan = toPlan || (user.plan as SkedPlanCodeName);
  numberOfAccounts = numberOfAccounts || currentAccountCount;
  const foundPlan = findPlanByPlanCodeName(plans, period, resolvedToPlan);
  if (!foundPlan) {
    return null;
  }

  return (
    <BaseUpsellModal>
      {(() => {
        switch (step) {
          case "order-summary": {
            return (
              <UpsellOrderSummary
                context={{
                  currency: subscription.currency_code as PlanCurrency,
                  periodUnit: period,
                  numberOfAccounts: numberOfAccounts,
                  planId: foundPlan.id,
                  planName: resolvedToPlan,
                  extraAccountPrice: foundPlan.additionalAccountsPrice,
                  isDowngrade: downgrade,
                }}
                onSuccess={({ numberOfAccounts }, subscription) => {
                  updateUserPlanContext({
                    numberOfAccounts,
                    toPlan: resolvedToPlan,
                    subscription,
                  });

                  setStep("result");
                }}
              />
            );
          }
          case "result": {
            return (
              <UpsellOrderSuccess
                feature={isChangingToYearly ? "annual" : "none"}
                planName={foundPlan.planName}
                downgrade={downgrade}
              />
            );
          }
          default: {
            return null;
          }
        }
      })()}
    </BaseUpsellModal>
  );
};

export const UpsellModal = ({
  modalType,
  triggerFeature = "approvals",
  toPlan,
  period,
  numberOfAccounts,
  downgrade,
}: {
  modalType: UpsellModalType;
  triggerFeature?: UpsellFeatureType;
  toPlan?: SkedPlanCodeName;
  period?: PlanPeriodUnit;
  numberOfAccounts?: number;
  downgrade: boolean;
}) => {
  const user = useSelector(getUser) as User;
  switch (modalType) {
    case "upsell": {
      return (
        <PlanUpsellModal
          isLegacyPlan={isLegacyPlan(user)}
          triggerFeature={triggerFeature}
          downgrade={downgrade}
        />
      );
    }
    case "upsell-confirm": {
      return (
        <PlanUpsellDirectModal
          toPlan={toPlan}
          period={period}
          numberOfAccounts={numberOfAccounts}
          downgrade={downgrade}
        />
      );
    }
    case "legacy-upsell-generic": {
      return (
        <BaseUpsellModal>
          <GenericLegacyPlanContent />
        </BaseUpsellModal>
      );
    }
    case "trial": {
      return <UpsellTrial feature={triggerFeature} />;
    }

    default: {
      return <AddAccountUpsellModal />;
    }
  }
};

const BaseUpsellModal = ({
  children,
  showClose = true,
}: {
  children: React.ReactNode;
  showClose?: boolean;
}) => {
  const dispatch = useDispatch();
  const close = () => dispatch(hideModal());

  return (
    <ModalComponent
      show={true}
      onHide={close}
      backdrop="static"
      zIndexOffset={UPSELL_MODAL_Z_INDEX_OFFSET}
    >
      {showClose && <StyledCloseButton onClick={close} />}
      {children}
    </ModalComponent>
  );
};

const FixedContainer = styled.div`
  height: 400px;
  position: relative;
`;

const StyledCloseButton = styled(Modal.CloseButton)`
  position: absolute;
  right: 16px;
  top: 16px;
`;

const ModalComponent = styled(Modal)`
  &.modal-dialog {
    font-size: 14px;
    max-width: 1176px;
    padding: 0;
    position: absolute;
    left: 50% !important; /** must use !important to override bootstrap*/
    top: 50% !important;
    margin: auto auto !important;
    transform: translate(-50%, -50%) !important;
    & > .modal-content {
      padding: 32px;
    }
  }
`;
